import { Association, type OrganizationManager, User } from "@entities";
import {
  associationRepo,
  organizationManagerRepo,
  organizationRepo,
  userRepo,
} from "@repositories";
// Import { CommunityController } from "@shared/controllers/Community.controller"; // Not used
import { BaseRoles } from "@shared/Roles";
import { phoneToIsraelSignedPhone } from "@shared/helpers/phone-to-israel-signed-phone";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { useAlert } from "../contexts/AlertProvider";
import { useOrganization } from "../contexts/OrganizationProvider";
import { useUser } from "../contexts/UserProvider";
import { useCommunities } from "./communities.query";
import { OrganizationRoles } from "@shared/Roles";
import { type HyperTableDef } from "@shared/frontend/hyper-table";

export function useVolunteers(searchParam: string | null = null) {
  const { currentUser } = useUser();
  const { currOrganization } = useOrganization();
  const { data, ...query } = useQuery({
    queryKey: ["volunteers", currentUser?.id],
    queryFn: useCallback(async () => {
      const users = await userRepo.find({
        where: User.hyperFilter({
          associations: {
            $some: {
              organizationId: currOrganization!.id,
              roles: {
                $some: {
                  $contains: OrganizationRoles.Volunteer,
                },
              },
            }
          }
        }),
        include: {
          associations: {
            where: {
              organizationId: currOrganization!.id,
            },
          },
        },
      });
      return { volunteers: users };
    }, [currOrganization]),
    enabled: !!currOrganization,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const allVolunteers = data?.volunteers;

  return {
    volunteers: allVolunteers,
    ...query,
  };
}

export function useOrganizationManagerOptions() {
  const { currOrganization } = useOrganization();
  return useQuery({
    queryKey: ["organization-manager-options", currOrganization?.id],
    queryFn: async () => {
      return organizationManagerRepo.find({
        where: { organization: { $id: currOrganization!.id } },
        orderBy: { name: "asc" },
        limit: 300,
      });
    },
    enabled: !!currOrganization?.id,
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });
}

export function useManagers() {
  const { currOrganization } = useOrganization();
  const { data: managers = [], ...query } = useQuery({
    queryKey: ["managers"],
    queryFn: async () => {
      const organization = await organizationRepo.findFirst({
        id: currOrganization!.id,
      });
      const managers = await organizationManagerRepo.find({
        where: { organization },
        include: { teams: { include: { team: true } } },
        orderBy: { name: "asc" },
        limit: 300,
      });
      return [...managers];
    },
    enabled: !!currOrganization,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  return {
    managers,
    ...query,
  };
}

export function useAddEditManager() {
  const { setAlertText, setSeverity } = useAlert();
  const { communities } = useCommunities();
  const { currentUser } = useUser();
  const { data: newCommunity, ...mutation } = useMutation({
    mutationKey: [currentUser?.id],
    mutationFn: async (options: {
      name: string;
      teams: string;
      phoneNumber: string;
      role: string;
      voicenterExtension?: string;
      hyperTable?: HyperTableDef<OrganizationManager>;
    }) => {
      const {
        teams,
        name,
        phoneNumber,
        role,
        voicenterExtension,
        hyperTable,
      } = options;
      const formattedPhone = phoneToIsraelSignedPhone(phoneNumber);
      if (!name || !phoneNumber) {
        setAlertText("יש למלא את כל השדות");
        return;
      }

      const phoneRegex = /^\+972\d{9}$/;
      if (!phoneRegex.test(formattedPhone)) {
        return setAlertText("מספר הטלפון אינו תקין");
      }

      const organization = await organizationRepo.findOne({
        where: { id: currentUser?.organization!.id },
      });
      const ext = voicenterExtension?.trim();
      const manager = await organizationManagerRepo.insert({
        name,
        phoneNumber: formattedPhone,
        roles: [BaseRoles.Manager],
        organization,
        role,
        profileImageId: "33b0cfec-11a3-4b34-ba81-b12bfce4e931",
        ...(ext ? { voicenterExtension: ext } : {}),
      });
      await organizationManagerRepo
        .relations(manager)
        .teams.insert({ team: communities?.find((com) => com.id === teams) });
      hyperTable?.context.current?.refetch("remove");
    },
    onSuccess: () => {
      setAlertText("המנהל נוסף בהצלחה");
      setSeverity("success");
    },
    onError: () => {
      setAlertText("קרתה תקלה , נסו שוב");
      setSeverity("error");
    },
  });

  return {
    newCommunity,
    ...mutation,
  };
}
